import { makeAutoObservable } from "mobx";

export interface DoctorClinicJoinContext {
  /** Display name of the doctor being linked (may be empty). */
  doctorName: string;
  /** Official LANR of the doctor (may be empty). */
  doctorLANR: string;
  /** Display name of the clinic (may be empty). */
  clinicName: string;
  /** Official BSNR of the clinic (may be empty). */
  clinicBSNR: string;
}

/**
 * Global, promise-based confirmation shown when an Rx dialog is about to create
 * a NEW doctor↔clinic association (the doctor is not yet linked to the selected
 * clinic). The join used to be created silently; now the user must confirm so a
 * mistyped LANR resolving to the wrong doctor can be caught and cancelled.
 *
 * `requestConfirm` opens the dialog and resolves to `true` (link) or `false`
 * (cancel). Singleton, mirroring the notification/lunch-break prompt pattern.
 */
class DoctorClinicJoinPromptStore {
  open = false;
  context: DoctorClinicJoinContext | null = null;
  resolver: ((confirmed: boolean) => void) | null = null;

  constructor() {
    makeAutoObservable(this, { resolver: false });
  }

  requestConfirm(context: DoctorClinicJoinContext): Promise<boolean> {
    this.context = context;
    this.open = true;
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  private settle(confirmed: boolean) {
    const resolve = this.resolver;
    this.resolver = null;
    this.context = null;
    this.open = false;
    resolve?.(confirmed);
  }

  confirm() {
    this.settle(true);
  }

  cancel() {
    this.settle(false);
  }
}

const doctorClinicJoinPromptStore = new DoctorClinicJoinPromptStore();
export default doctorClinicJoinPromptStore;
